/* eslint-disable react-refresh/only-export-components */
import { RotateCcw, Save, Shield, UserCog, User } from "lucide-react";
import {
  EditBase,
  Form,
  useGetList,
  useNotify,
  useTranslate,
  useGetIdentity,
  useDataProvider,
} from "ra-core";
import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { TextInput } from "@/components/admin/text-input";
import type { Sale, UserRole } from "../providers/fakerest/dataGenerator/types";

import ImageEditorField from "../misc/ImageEditorField";
import {
  useConfigurationContext,
  useConfigurationUpdater,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";
import { defaultConfiguration } from "../root/defaultConfiguration";

const SECTIONS = [
  {
    id: "company",
    label: "crm.settings.sections.company",
    fallback: "Company",
  },
  {
    id: "branding",
    label: "crm.settings.sections.branding",
    fallback: "Branding",
  },
  {
    id: "users",
    label: "crm.settings.sections.users",
    fallback: "Users",
  },
];

const transformFormValues = (data: Record<string, any>) => ({
  config: {
    // Branding
    title: data.title,
    lightModeLogo: data.lightModeLogo,
    darkModeLogo: data.darkModeLogo,
    favicon: data.favicon,
    // Company
    companyName: data.companyName,
    companyAddress: data.companyAddress,
    companyPhone: data.companyPhone,
    companyEmail: data.companyEmail,
    companyWebsite: data.companyWebsite,
  } as ConfigurationContextValue,
});

export const SettingsPage = () => {
  const updateConfiguration = useConfigurationUpdater();
  const notify = useNotify();

  return (
    <EditBase
      resource="configuration"
      id={1}
      mutationMode="pessimistic"
      redirect={false}
      transform={transformFormValues}
      mutationOptions={{
        onSuccess: (data: any) => {
          updateConfiguration(data.config);
          notify("crm.settings.saved");
        },
        onError: () => {
          notify("crm.settings.save_error", {
            type: "error",
          });
        },
      }}
    >
      <SettingsForm />
    </EditBase>
  );
};

SettingsPage.path = "/settings";

const SettingsForm = () => {
  const config = useConfigurationContext();

  const defaultValues = useMemo(
    () => ({
      // Branding
      title: config.title,
      lightModeLogo: { src: config.lightModeLogo },
      darkModeLogo: { src: config.darkModeLogo },
      favicon: { src: config.favicon },
      // Company
      companyName: config.companyName,
      companyAddress: config.companyAddress,
      companyPhone: config.companyPhone,
      companyEmail: config.companyEmail,
      companyWebsite: config.companyWebsite,
    }),
    [config],
  );

  return (
    <Form defaultValues={defaultValues}>
      <SettingsFormFields />
    </Form>
  );
};

const SettingsFormFields = () => {
  const translate = useTranslate();
  const {
    reset,
    formState: { isSubmitting },
  } = useFormContext();

  return (
    <div className="flex gap-8 mt-4 pb-20">
      {/* Left navigation */}
      <nav className="hidden md:block w-48 shrink-0">
        <div className="sticky top-4 space-y-1">
          <h1 className="text-2xl font-semibold px-3 mb-2">
            {translate("crm.settings.title")}
          </h1>
          {SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => {
                document
                  .getElementById(section.id)
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="block w-full text-left px-3 py-1 text-sm rounded-md hover:text-foreground hover:bg-muted transition-colors"
            >
              {translate(section.label, { smart_count: 2 })}
            </button>
          ))}
        </div>
      </nav>

      {/* Main content */}
      <div className="flex-1 min-w-0 max-w-2xl space-y-6">
        {/* Company */}
        <Card id="company">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("crm.settings.sections.company", {
                _: "Company",
              })}
            </h2>
            <TextInput
              source="companyName"
              label="crm.settings.company.name"
              helperText={false}
            />
            <TextInput
              source="companyAddress"
              label="crm.settings.company.address"
              helperText={false}
            />
            <div className="grid grid-cols-2 gap-4">
              <TextInput
                source="companyPhone"
                label="crm.settings.company.phone"
                helperText={false}
              />
              <TextInput
                source="companyEmail"
                label="crm.settings.company.email"
                helperText={false}
              />
            </div>
            <TextInput
              source="companyWebsite"
              label="crm.settings.company.website"
              helperText={false}
            />
          </CardContent>
        </Card>

        {/* Branding */}
        <Card id="branding">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("crm.settings.sections.branding")}
            </h2>
            <TextInput source="title" label="crm.settings.app_title" />
            <div className="flex gap-8">
              <div className="flex flex-col items-center gap-1">
                <p className="text-sm text-muted-foreground">
                  {translate("crm.settings.light_mode_logo")}
                </p>
                <ImageEditorField
                  source="lightModeLogo"
                  width={100}
                  height={100}
                  linkPosition="bottom"
                  backgroundImageColor="#f5f5f5"
                />
              </div>
              <div className="flex flex-col items-center gap-1">
                <p className="text-sm text-muted-foreground">
                  {translate("crm.settings.dark_mode_logo")}
                </p>
                <ImageEditorField
                  source="darkModeLogo"
                  width={100}
                  height={100}
                  linkPosition="bottom"
                  backgroundImageColor="#1a1a1a"
                />
              </div>
              <div className="flex flex-col items-center gap-1">
                <p className="text-sm text-muted-foreground">
                  {translate("crm.settings.favicon", { _: "Favicon" })}
                </p>
                <ImageEditorField
                  source="favicon"
                  width={48}
                  height={48}
                  linkPosition="bottom"
                  backgroundImageColor="#f5f5f5"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Users */}
        <UsersSection />
      </div>

      {/* Sticky save button */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-background p-4">
        <div className="max-w-screen-xl mx-auto flex gap-8 px-4">
          <div className="hidden md:block w-48 shrink-0" />
          <div className="flex-1 min-w-0 max-w-2xl flex justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                reset({
                  ...defaultConfiguration,
                  lightModeLogo: {
                    src: defaultConfiguration.lightModeLogo,
                  },
                  darkModeLogo: { src: defaultConfiguration.darkModeLogo },
                })
              }
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              {translate("crm.settings.reset_defaults")}
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.history.back()}
              >
                {translate("ra.action.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                <Save className="h-4 w-4 mr-1" />
                {isSubmitting
                  ? translate("crm.settings.saving")
                  : translate("ra.action.save")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/** A minimal color picker input compatible with ra-core's useInput. */
const ColorInput = ({ source }: { source: string }) => {
  const { field } = useInput({ source });
  return (
    <input
      type="color"
      {...field}
      value={field.value || "#000000"}
      className="w-9 h-9 shrink-0 cursor-pointer appearance-none rounded border bg-transparent p-0.5 [&::-webkit-color-swatch-wrapper]:cursor-pointer [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:cursor-pointer [&::-webkit-color-swatch]:rounded-sm [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:cursor-pointer [&::-moz-color-swatch]:rounded-sm [&::-moz-color-swatch]:border-none"
    />
  );
};

/** Role badge styling */
const ROLE_STYLES: Record<
  UserRole,
  { label: string; className: string; icon: typeof Shield }
> = {
  admin: {
    label: "Admin",
    className: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200",
    icon: Shield,
  },
  manager: {
    label: "Manager",
    className:
      "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200",
    icon: UserCog,
  },
  sales_rep: {
    label: "Sales Rep",
    className:
      "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200",
    icon: User,
  },
};

/** Users management section - admin only */
const UsersSection = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { data: identity } = useGetIdentity();
  const { data: users, refetch } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
  });

  const currentUser = identity as Sale | undefined;
  const isAdmin = currentUser?.role === "admin";

  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);

  const handleRoleChange = async (userId: number, newRole: UserRole) => {
    setUpdatingUserId(userId);
    try {
      await dataProvider.update("sales", {
        id: userId,
        data: { role: newRole },
        previousData: users?.find((u) => u.id === userId),
      });
      notify("User role updated", { type: "success" });
      refetch();
    } catch {
      notify("Failed to update user role", { type: "error" });
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleDisableToggle = async (userId: number, disabled: boolean) => {
    setUpdatingUserId(userId);
    try {
      await dataProvider.update("sales", {
        id: userId,
        data: { disabled },
        previousData: users?.find((u) => u.id === userId),
      });
      notify(disabled ? "User disabled" : "User enabled", { type: "success" });
      refetch();
    } catch {
      notify("Failed to update user status", { type: "error" });
    } finally {
      setUpdatingUserId(null);
    }
  };

  // Only admins can see user management
  if (!isAdmin) {
    return null;
  }

  return (
    <Card id="users">
      <CardContent className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("crm.settings.sections.users", { _: "Users" })}
        </h2>
        <p className="text-sm text-muted-foreground">
          Manage user roles and access permissions.
        </p>

        <div className="space-y-3">
          {users?.map((user) => {
            const roleStyle = ROLE_STYLES[user.role];
            const RoleIcon = roleStyle.icon;
            const isCurrentUser = user.id === currentUser?.id;
            const isUpdating = updatingUserId === user.id;

            return (
              <div
                key={user.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  user.disabled ? "opacity-50 bg-muted" : "bg-card"
                }`}
              >
                <div className="flex items-center gap-3">
                  {user.avatar?.src ? (
                    <img
                      src={user.avatar.src}
                      alt={`${user.first_name} ${user.last_name}`}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <User className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {user.first_name} {user.last_name}
                      {isCurrentUser && (
                        <Badge variant="outline" className="text-xs">
                          You
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {user.email}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Badge className={roleStyle.className}>
                    <RoleIcon className="h-3 w-3 mr-1" />
                    {roleStyle.label}
                  </Badge>

                  {!isCurrentUser && (
                    <>
                      <Select
                        value={user.role}
                        onValueChange={(value) =>
                          handleRoleChange(user.id, value as UserRole)
                        }
                        disabled={isUpdating}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="sales_rep">Sales Rep</SelectItem>
                        </SelectContent>
                      </Select>

                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!user.disabled}
                          onCheckedChange={(checked) =>
                            handleDisableToggle(user.id, !checked)
                          }
                          disabled={isUpdating}
                        />
                        <span className="text-sm text-muted-foreground">
                          {user.disabled ? "Disabled" : "Active"}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
